import { captureException } from '@sentry/remix'
import type { Area } from '~/features/facets'
import {
  LargeAreas,
  type MediumArea,
  type MinimumArea,
  type SmallArea,
  findMinimum,
  findSmall,
  isMediumArea,
  isMinimumArea,
  isSmallArea,
} from '~/features/facets/types'
import { isEmpty } from '~/lib/empty'
import type {
  CityId,
  LargeAreaCode,
  MediumAreaCode,
  MinimumAreaCode,
  SmallAreaCode,
  SpotId,
  TownId,
} from '~/lib/graphql/scalars'
import type { SearchCriteria } from '../validate'
import type { Facet } from './type'

export function areaCodesToFacets(
  mediumArea: MediumArea | undefined,
  criteria?: SearchCriteria
): Facet[] {
  if (mediumArea === undefined) {
    return []
  }
  // 小エリア・極小エリアの指定が無いときだけ中エリアの Facet を作る
  if (isEmpty(criteria?.smallAreaCodes) && isEmpty(criteria?.minimumAreaCodes)) {
    return [mediumAreaToFacet(mediumArea)]
  }
  const smalls = (criteria?.smallAreaCodes ?? [])
    .map((code) => findSmall(mediumArea, code))
    .filter((s) => s !== undefined)
  const minimums = (criteria?.minimumAreaCodes ?? [])
    .map((code) => findMinimum(mediumArea, code))
    .filter((m) => m !== undefined)
  const facets = [
    ...smalls.map((s) => smallAreaToFacet(s, mediumArea)),
    ...minimums.map((m) => minimumAreaToFacet(m, findSmall(mediumArea, m.small_area_code)!)),
  ]
    .filter((f) => f !== undefined)
    .filter((f) => f.showInList(criteria))
  // alphabet の範囲だから locale 指定なしの localCompare で大丈夫
  return facets.toSorted((a, b) => a.facetKey.localeCompare(b.facetKey))
}

function mediumAreaToFacet(medium: MediumArea): Facet {
  return {
    facetType: 'mediumArea',
    ...areaFacetBase(medium),
    add(current) {
      // ユースケースが無いため、呼ばれることはあり得ない
      // 万が一呼ばれた場合は Sentry でエラーを通知する
      captureException(new Error('Unexpected call to mediumAreaToFacet.add'), {
        extra: { current },
      })
      return current ?? {}
    },
    remove(current) {
      // エリアに関する検索条件はすべて削除する
      const {
        largeAreaCode,
        mediumAreaCode,
        smallAreaCodes,
        minimumAreaCodes,
        cityId,
        townId,
        spotId,
        radius,
        aroundHotel,
        ...removed
      } = current ?? {}
      return removed
    },
    selected(criteria) {
      return (
        criteria?.largeAreaCode === medium.large_area_code &&
        criteria?.mediumAreaCode === medium.code
      )
    },
    showInList(criteria) {
      return this.selected(criteria)
    },
  }
}

export function areaCodeToFacet(
  code: string,
  mediumArea: MediumArea | undefined
): Facet | undefined {
  if (mediumArea === undefined) {
    return undefined
  }
  const minimum = findMinimum(mediumArea, code as MinimumAreaCode)
  if (minimum !== undefined) {
    const small = findSmall(mediumArea, minimum.small_area_code)
    if (small !== undefined) {
      return minimumAreaToFacet(minimum, small)
    }
  }
  const small = findSmall(mediumArea, code as SmallAreaCode)
  if (small !== undefined) {
    return smallAreaToFacet(small, mediumArea)
  }
  return undefined
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('areaCodeToFacets', async () => {
    const all = await import('../../../facets/data/areas.json').then((m) =>
      LargeAreas.parse(m.default)
    )
    const yokohama = all.find((a) => a.code === '03')?.mediums.find((m) => m.code === '002')!

    it('極小エリアが紐づかない小エリア', () => {
      const facet = areaCodeToFacet('003', yokohama)
      expect(facet?.label).toBe('横浜駅周辺')
      expect(facet?.facetKey).toBe('area.03002.003')
      expect(facet?.facetType).toBe('smallArea')
    })

    it('極小エリアが紐づく小エリア', () => {
      const facet = areaCodeToFacet('001', yokohama)
      expect(facet?.label).toBe('みなとみらい・桜木町')
      expect(facet?.facetKey).toBe('area.03002.001')
      expect(facet?.facetType).toBe('smallArea')
    })

    it('極小エリア', () => {
      const facet = areaCodeToFacet('0201', yokohama)
      expect(facet?.label).toBe('みなとみらい')
      expect(facet?.facetKey).toBe('area.03002.001.0201')
      expect(facet?.facetType).toBe('minimumArea')
    })
  })
}

export function smallAreaToFacet(small: SmallArea, medium?: MediumArea): Facet {
  return {
    facetType: 'smallArea',
    ...areaFacetBase(small),
    // その他エリアなら都道府県名を頭につける
    label:
      small.code === '999' && medium !== undefined ? `${medium.name}${small.name}` : small.name,
    add(current) {
      // エリアに関する検索条件は一つしか指定できないため、既に指定されている場合は削除する
      const {
        cityId,
        townId,
        smallAreaCodes,
        minimumAreaCodes,
        radius,
        aroundHotel,
        spotId,
        ...removed
      } = current ?? {}
      // 都道府県（中エリア）が異なる場合は入れ替える
      // 中エリアは一つしか指定できないため
      if (!isSameMediumArea(current, small)) {
        return {
          ...removed,
          largeAreaCode: small.large_area_code,
          mediumAreaCode: small.medium_area_code,
          smallAreaCodes: [small.code],
          minimumAreaCodes: small.minimums.map((m) => m.code),
        }
      }
      const smalls = new Set(smallAreaCodes)
      const minimums = new Set(minimumAreaCodes)
      smalls.add(small.code as SmallAreaCode)
      for (const m of small.minimums) {
        minimums.add(m.code)
      }
      return {
        ...removed,
        smallAreaCodes: Array.from(smalls).toSorted(),
        minimumAreaCodes: Array.from(minimums).toSorted(),
      }
    },

    remove(current) {
      if (!isSameMediumArea(current, small)) {
        return current ?? {}
      }
      const { cityId, townId, smallAreaCodes, minimumAreaCodes, ...removed } = current ?? {}
      const smalls = new Set(smallAreaCodes)
      smalls.delete(small.code)
      const minimums = new Set(minimumAreaCodes)
      for (const m of small.minimums) {
        minimums.delete(m.code)
      }
      if (smalls.size > 0 && minimums.size > 0) {
        return {
          ...removed,
          smallAreaCodes: Array.from(smalls).toSorted(),
          minimumAreaCodes: Array.from(minimums).toSorted(),
        }
      } else if (smalls.size > 0) {
        return {
          ...removed,
          smallAreaCodes: Array.from(smalls).toSorted(),
        }
      } else if (minimums.size > 0) {
        return {
          ...removed,
          minimumAreaCodes: Array.from(minimums).toSorted(),
        }
      } else {
        return removed
      }
    },

    selected(criteria) {
      return criteria?.smallAreaCodes?.includes(small.code) ?? false
    },

    showInList(criteria) {
      return this.selected(criteria)
    },
  }
}

export function minimumAreaToFacet(minimum: MinimumArea, small: SmallArea): Facet {
  return {
    facetType: 'minimumArea',
    ...areaFacetBase(minimum),
    add(current) {
      // エリアに関する検索条件は一つしか指定できないため、既に指定されている場合は削除する
      const {
        cityId,
        townId,
        smallAreaCodes,
        minimumAreaCodes,
        radius,
        aroundHotel,
        spotId,
        ...removed
      } = current ?? {}
      // 異なる都道府県の場合は置き換える
      if (!isSameMediumArea(current, minimum)) {
        return {
          ...removed,
          largeAreaCode: minimum.large_area_code,
          mediumAreaCode: minimum.medium_area_code,
          minimumAreaCodes: [minimum.code],
        }
      }
      const minimums = new Set(minimumAreaCodes)
      minimums.add(minimum.code)
      // 同じ小エリアに所属する極小エリアが全部揃ったら、小エリアも選択状態にする
      const completed = small.minimums.every((m) => minimums.has(m.code))
      if (completed) {
        const smalls = new Set(smallAreaCodes)
        smalls.add(small.code)
        return {
          ...removed,
          smallAreaCodes: Array.from(smalls).toSorted(),
          minimumAreaCodes: Array.from(minimums).toSorted(),
        }
      }
      if (smallAreaCodes === undefined || smallAreaCodes.length === 0) {
        return {
          ...removed,
          minimumAreaCodes: Array.from(minimums).toSorted(),
        }
      }
      return {
        ...removed,
        smallAreaCodes,
        minimumAreaCodes: Array.from(minimums).toSorted(),
      }
    },

    remove(current) {
      // 異なる都道府県の場合は無視、基本はありえない
      if (!isSameMediumArea(current, minimum)) {
        return current ?? {}
      }
      const { cityId, townId, smallAreaCodes, minimumAreaCodes, ...removed } = current ?? {}
      // 極小エリアを外すとき、親の小エリアも外す
      // 「京都駅周辺」が選択状態で「京都駅」が外れたら「京都駅周辺」も外れる
      const smalls = new Set(smallAreaCodes)
      const minimums = new Set(minimumAreaCodes)
      smalls.delete(small.code)
      minimums.delete(minimum.code)
      if (smalls.size === 0) {
        return {
          ...removed,
          minimumAreaCodes: Array.from(minimums).toSorted(),
        }
      }
      return {
        ...removed,
        smallAreaCodes: Array.from(smalls).toSorted(),
        minimumAreaCodes: Array.from(minimums).toSorted(),
      }
    },

    selected(criteria) {
      return criteria?.minimumAreaCodes?.includes(minimum.code) ?? false
    },

    // 同じ小エリアに所属する極小エリアが全部揃っていないときだけ出す
    showInList(criteria) {
      const minimums = new Set(criteria?.minimumAreaCodes)
      const completed = small.minimums.every((m) => minimums.has(m.code))
      return !completed
    },
  }
}

function areaFacetBase(area: Area): Pick<Facet, 'label' | 'facetKey'> {
  return {
    label: area.name,
    facetKey: areaToFacetKey(area),
  }
}

export function areaToFacetKey(area: Area): string {
  if (isMediumArea(area)) {
    return `area.${area.large_area_code}${area.code}`
  } else if (isSmallArea(area)) {
    return `area.${area.large_area_code}${area.medium_area_code}.${area.code}`
  } else if (isMinimumArea(area)) {
    return `area.${area.large_area_code}${area.medium_area_code}.${area.small_area_code}.${area.code}`
  } else {
    throw new Error('Unexpected area type')
  }
}

function isSameMediumArea(
  current: SearchCriteria | undefined,
  area: SmallArea | MinimumArea
): boolean {
  const { largeAreaCode, mediumAreaCode } = current ?? {
    largeAreaCode: '',
    mediumAreaCode: '',
  }
  return `${largeAreaCode}${mediumAreaCode}` === `${area.large_area_code}${area.medium_area_code}`
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe('area facets', async () => {
    const all = await import('../../../facets/data/areas.json').then((m) =>
      LargeAreas.parse(m.default)
    )
    const kyoto = all.find((a) => a.code === '07')?.mediums.find((m) => m.code === '002')!
    const kyotoStationNearby = kyoto.smalls.find((s) => s.code === '001')!
    const kyotoStation = kyotoStationNearby.minimums.find((m) => m.code === '0094')!

    describe('mediumAreaFacet', async () => {
      const facet = mediumAreaToFacet(kyoto)

      it('facetKey', () => {
        expect(facet.facetKey).toBe('area.07002')
      })

      describe('selected', () => {
        it('選択されている', () => {
          expect(
            facet.selected({
              largeAreaCode: '07' as LargeAreaCode,
              mediumAreaCode: '002' as MediumAreaCode,
            })
          ).toBe(true)
        })
        it('選択されていない', () => {
          expect(
            facet.selected({
              largeAreaCode: '03' as LargeAreaCode,
              mediumAreaCode: '001' as MediumAreaCode,
            })
          ).toBe(false)
        })
        it('空っぽ', () => {
          expect(facet.selected({})).toBe(false)
        })
      })

      describe('add', () => {
        it('何も変わらない（ユースケースが存在しないので実行されること自体あり得ない）', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
          }
          const next = facet.add(current)
          expect(next).toStrictEqual(current)
        })
      })

      describe('remove', () => {
        it('未指定状態なら何も変わらない', () => {
          const current: SearchCriteria = {}
          const next = facet.remove(current)
          expect(next).toStrictEqual({})
        })
        it('すべてのエリアに関する検索条件を削除する', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            smallAreaCodes: ['001', '002'] as SmallAreaCode[],
            minimumAreaCodes: ['0094', '0095', '0096'] as MinimumAreaCode[],
            cityId: '26106' as CityId,
            townId: '6008001001' as TownId,
            spotId: '3828' as SpotId,
            radius: 0.5,
            aroundHotel: true,
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual({})
        })
      })
    })

    describe('smallAreaFacet', async () => {
      const facet = smallAreaToFacet(kyotoStationNearby, kyoto)

      it('facetKey', () => {
        expect(facet.facetKey).toBe('area.07002.001')
      })

      describe('selected', () => {
        it('選択されている', () => {
          expect(facet.selected({ smallAreaCodes: ['001', '002'] as SmallAreaCode[] })).toBe(true)
        })
        it('選択されていない', () => {
          expect(facet.selected({ smallAreaCodes: ['002'] as SmallAreaCode[] })).toBe(false)
        })
        it('空っぽ', () => {
          expect(facet.selected({})).toBe(false)
        })
      })

      describe('add', () => {
        it('未指定状態で新しく追加する', () => {
          const current: SearchCriteria = {}
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            smallAreaCodes: ['001'],
            minimumAreaCodes: ['0094', '0095', '0096'],
          })
        })

        it('既に追加されているので何も変わらない', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            smallAreaCodes: ['001', '002'] as SmallAreaCode[],
            minimumAreaCodes: ['0094', '0095', '0096', '0097'] as MinimumAreaCode[],
          }
          const next = facet.add(current)
          expect(next).toStrictEqual(current)
        })

        it('他の都道府県のエリアが選択されているので置き換える', () => {
          const current: SearchCriteria = {
            largeAreaCode: '03' as LargeAreaCode,
            mediumAreaCode: '001' as MediumAreaCode,
            smallAreaCodes: ['001', '002'] as SmallAreaCode[],
            minimumAreaCodes: ['0001', '0002', '0003'] as MinimumAreaCode[],
          }
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            smallAreaCodes: ['001'],
            minimumAreaCodes: ['0094', '0095', '0096'],
          })
        })

        it('spotが選択されているので置き換える', () => {
          const current: SearchCriteria = {
            spotId: '663' as SpotId,
            aroundHotel: true,
          }
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            smallAreaCodes: ['001'],
            minimumAreaCodes: ['0094', '0095', '0096'],
          })
        })
      })

      describe('remove', () => {
        it('未指定状態なら何も変わらない', () => {
          const current: SearchCriteria = {}
          const next = facet.remove(current)
          expect(next).toStrictEqual({})
        })

        it('他の都道府県が選択されているので何も変わらない(ありえない状態)', () => {
          const current: SearchCriteria = {
            largeAreaCode: '03' as LargeAreaCode,
            mediumAreaCode: '001' as MediumAreaCode,
            smallAreaCodes: ['001', '002'] as SmallAreaCode[],
            minimumAreaCodes: ['0001', '0002', '0003'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual(current)
        })

        it('最後の小エリアなので検索条件から消える', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            smallAreaCodes: ['001'] as SmallAreaCode[],
            minimumAreaCodes: ['0094', '0095', '0096'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
          })
        })

        it('他の小エリアが選択されているので、そちらは残る', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            smallAreaCodes: ['001', '002'] as SmallAreaCode[],
            minimumAreaCodes: ['0094', '0095', '0096'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            smallAreaCodes: ['002'],
          })
        })

        it('他の極小エリアが選択されているので、そちらは残る', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            smallAreaCodes: ['001'] as SmallAreaCode[],
            minimumAreaCodes: ['0094', '0095', '0096', '0097'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            minimumAreaCodes: ['0097'],
          })
        })
      })
    })

    describe('minimumAreaFacet', async () => {
      const facet = minimumAreaToFacet(kyotoStation, kyotoStationNearby)

      it('facetKey', () => {
        expect(facet.facetKey).toBe('area.07002.001.0094')
      })

      describe('selected', () => {
        it('選択されている', () => {
          expect(
            facet.selected({
              minimumAreaCodes: ['0094', '0095'] as MinimumAreaCode[],
            })
          ).toBe(true)
        })
        it('選択されていない', () => {
          expect(facet.selected({ minimumAreaCodes: ['0095'] as MinimumAreaCode[] })).toBe(false)
        })
        it('空っぽ', () => {
          expect(facet.selected({})).toBe(false)
        })
      })

      describe('add', () => {
        it('都道府県が違えば置き換え', () => {
          const current: SearchCriteria = {
            largeAreaCode: '03' as LargeAreaCode,
            mediumAreaCode: '001' as MediumAreaCode,
            minimumAreaCodes: ['0001', '0002'] as MinimumAreaCode[],
          }
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            minimumAreaCodes: ['0094'],
          })
        })

        it('未指定状態で新しく追加する', () => {
          const current: SearchCriteria = {}
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            minimumAreaCodes: ['0094'],
          })
        })

        it('他の極小エリアが選択されているので、追加する', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            minimumAreaCodes: ['0095'] as MinimumAreaCode[],
          }
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            minimumAreaCodes: ['0094', '0095'],
          })
        })

        it('同じ小エリア配下の極小エリアが揃ったら、その小エリアも選択状態となる', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            minimumAreaCodes: ['0095', '0096'] as MinimumAreaCode[],
          }
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            smallAreaCodes: ['001'],
            minimumAreaCodes: ['0094', '0095', '0096'],
          })
        })

        it('spotが選択されているので置き換える', () => {
          const current: SearchCriteria = {
            spotId: '663' as SpotId,
            aroundHotel: true,
          }
          const next = facet.add(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            minimumAreaCodes: ['0094'],
          })
        })
      })

      describe('remove', () => {
        it('都道府県が違えば無視', () => {
          const current: SearchCriteria = {
            largeAreaCode: '03' as LargeAreaCode,
            mediumAreaCode: '001' as MediumAreaCode,
            minimumAreaCodes: ['0001', '0002'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual(current)
        })

        it('選択されていないときは無視（ありえない状態）', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            minimumAreaCodes: ['0001', '0002'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual(current)
        })

        it('選択されている状態から外れる', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            minimumAreaCodes: ['0094', '0095'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            minimumAreaCodes: ['0095'],
          })
        })

        it('親の小エリアも外れる', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            smallAreaCodes: ['001'] as SmallAreaCode[],
            minimumAreaCodes: ['0094', '0095', '0096'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            minimumAreaCodes: ['0095', '0096'],
          })
        })

        it('他の小エリアは残る', () => {
          const current: SearchCriteria = {
            largeAreaCode: '07' as LargeAreaCode,
            mediumAreaCode: '002' as MediumAreaCode,
            smallAreaCodes: ['001', '002'] as SmallAreaCode[],
            minimumAreaCodes: ['0094', '0095', '0096', '0097', '0098'] as MinimumAreaCode[],
          }
          const next = facet.remove(current)
          expect(next).toStrictEqual({
            largeAreaCode: '07',
            mediumAreaCode: '002',
            smallAreaCodes: ['002'],
            minimumAreaCodes: ['0095', '0096', '0097', '0098'],
          })
        })
      })
    })

    describe('normalizeMinimumAreaCodes', () => {
      it('全部選択されている「京都駅周辺」以下は削除', () => {
        const mediumArea = kyoto
        const smallAreaCodes = ['001', '004'] as SmallAreaCode[]
        const minimumAreaCodes = ['0094', '0095', '0096', '0105'] as MinimumAreaCode[]
        const criteria: SearchCriteria = { smallAreaCodes, minimumAreaCodes }
        const facets = areaCodesToFacets(mediumArea, criteria).filter((f) => f.showInList(criteria))
        expect(facets.map((f) => f.facetKey)).toStrictEqual([
          'area.07002.001',
          'area.07002.004',
          'area.07002.004.0105',
        ])
      })
    })
  })
}
